import React, {useEffect, useState} from "react";
import userService from "../../../../../services/user.service";
import {useParams, useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {useForm} from "react-hook-form";
import {useEyeconxStore} from "../../../../../store/eyeconxStore";
import Swal from 'sweetalert2';
import {Tooltip} from 'react-tooltip';

const ClinicUserSettings = ({ setActiveTab }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [delMsg, setDelMsg] = useState("");
    const [userDeleted, setUserDeleted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const {clinicId} = useParams();
    const navigate = useNavigate();
    const selecteduser = useEyeconxStore((state) => state.selectedUser);
    const [user, setUser] = useState(null);
    const {
        register,
        handleSubmit,
        reset,
        formState: {errors, isDirty},
    } = useForm({
        defaultValues: {
            isAuthenticated: false
        },
        shouldUnregister: true // This helps with form state cleanup
    });
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        getUserInfoById();
        getRoles();
    }, [selecteduser]);

    const getRoles = () => {
        userService
            .getRoles()
            .then((res) => {
                setRoles(res.data);
            })
            .catch((err) => {
                console.log(err);
                toast.error("Failed to load roles", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            });
    };

    const getUserInfoById = () => {
        userService
            .getUser(selecteduser.clinicId, selecteduser.id)
            .then((res) => {
                setUser(res.data);
                // Convert boolean to string for the select input
                reset({
                    ...res.data,
                    isAuthenticated: res.data.isAuthenticated.toString() // Convert boolean to string
                });
            })
            .catch((err) => {
                console.log(err);
                toast.error("Failed to load user data", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            });
    };

    const handleDelChange = (e) => {
        setDelMsg(e.target.value);
    };


    const deleteUser = async (e) => {
        e.preventDefault();

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will permanently delete the user!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `
            <i class="bi bi-check"></i>
            Delete`,
            cancelButtonText: `
            <i class="bi bi-x"></i>
            Cancel`,
            input: 'text',
            inputLabel: 'Type "DELETE" to confirm',
            inputPlaceholder: 'Type DELETE here...',
            inputAttributes: {
                autocapitalize: 'off'
            },
            customClass: {
                confirmButton: `btn btn-success btn-sm me-2`,
                cancelButton: 'btn btn-secondary btn-sm',
                title: `card-title`,
                inputLabel: `small`,
                html: `small`
            },
            inputValidator: (value) => {
                if (value?.toLowerCase() !== 'delete') {
                    return 'You need to type "DELETE" to confirm';
                }
            }
        });

        if (!result.isConfirmed) return;

        try {
            setIsDeleting(true);
            await userService.deleteUser(user.id, clinicId);

            await Swal.fire(
                'Deleted!',
                'User has been deleted.',
                'success'
            );

            setUserDeleted(true);
            navigate(-1);
        } catch (err) {
            console.error("Delete error:", err);
            await Swal.fire(
                'Error!',
                err.response?.data?.message || "Error while deleting user",
                'error'
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const canDeleteUser = () => {
        if (!user) return false;
        return user.role !== "OTHER"; // Add other conditions as needed
    };

    const resetPassword = () => {
        setIsResetting(true);
        userService
            .passwordReset(clinicId, user.id)
            .then((res) => {
                toast.success("Password reset email sent successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Error sending password reset", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsResetting(false);
            });
    };

    const onSubmit = (data, e) => {
        setIsSubmitting(true);
        let newData = {
            ...data,
            isAuthenticated: data.isAuthenticated === "true"
        };

        if (!user.isAuthenticated && newData.isAuthenticated) {
            newData = {...newData, secret: "ECXuserx1y1z1"};
        }

        userService
            .updateUser(user.id, clinicId, newData)
            .then((res) => {
                toast.success("User updated successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
                // Navigate back to UsersTab
                setActiveTab("clinic-users-tab"); // Match the exact tab ID from your container
            })
            .catch((err) => {
                toast.error(err.response?.data?.errorMessage || "Error updating user", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (userDeleted) {
        return null; // Or redirect if needed
    }

    return (
        <>
            <div className="card-padding shadow-none">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="card-title" style={{color: "#012970"}}>
                        {" "}
                        <i className="bi bi-people"></i> Doctors / Managers / Staff
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-8">
                    <div className="card shadow-none card-padding">
                        <div className="card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label htmlFor="firstName" className="form-label">
                                            First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                            {...register("firstName", {
                                                required: "First name is required",
                                                minLength: {
                                                    value: 2,
                                                    message: "Minimum 2 characters required"
                                                }
                                            })}
                                            placeholder="Enter first name"
                                        />
                                        {errors.firstName && (
                                            <div className="invalid-feedback">
                                                {errors.firstName.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="lastName" className="form-label">
                                            Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                            {...register("lastName", {
                                                required: "Last name is required",
                                                minLength: {
                                                    value: 2,
                                                    message: "Minimum 2 characters required"
                                                }
                                            })}
                                            placeholder="Enter last name"
                                        />
                                        {errors.lastName && (
                                            <div className="invalid-feedback">
                                                {errors.lastName.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="email" className="form-label">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            {...register("email", {
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: "Invalid email address"
                                                }
                                            })}
                                            placeholder="Enter email"
                                        />
                                        {errors.email && (
                                            <div className="invalid-feedback">
                                                {errors.email.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="username" className="form-label">
                                            Username <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="username"
                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                            {...register("username", {
                                                required: "Username is required",
                                                minLength: {
                                                    value: 4,
                                                    message: "Minimum 4 characters required"
                                                }
                                            })}
                                            placeholder="Enter username"
                                        />
                                        {errors.username && (
                                            <div className="invalid-feedback">
                                                {errors.username.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="roleId" className="form-label">
                                            Role <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="role"
                                            className={`form-select ${errors.role ? 'is-invalid' : ''}`}
                                            {...register("role", {required: "Role is required"})}
                                        >
                                            <option value="">Select a role</option>
                                            {roles.map((item) => (
                                                <option key={item} value={item}>
                                                    {item}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.role && (
                                            <div className="invalid-feedback">
                                                {errors.role.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="isAuthenticated" className="form-label">
                                            Authentication Status <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="isAuthenticated"
                                            className={`form-select ${errors.isAuthenticated ? 'is-invalid' : ''}`}
                                            {...register("isAuthenticated", {
                                                required: "Authentication status is required",
                                                valueAsNumber: false // Ensure it's treated as string
                                            })}
                                        >
                                            <option value="false">No</option>
                                            <option value="true">Yes</option>
                                        </select>
                                    </div>

                                    <div className="col-12 mt-4">
                                        <div className="d-flex justify-content-end gap-2">
                                            <button
                                                type="submit"
                                                className="btn btn-success btn-sm"
                                                disabled={isSubmitting || !isDirty}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"
                                                              role="status" aria-hidden="true"></span>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-floppy"></i> Update
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card card-padding shadow-none card-border mb-4">
                        <div className="card-title">
                            <i className="bi bi-gear"></i> User Actions
                        </div>
                        <div className="card-body">
                            {user?.isAuthenticated && (
                                <button
                                    className="btn btn-outline-success w-100 mb-3"
                                    onClick={resetPassword}
                                    disabled={isResetting}
                                >
                                    {isResetting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"
                                                  aria-hidden="true"></span>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-envelope-arrow-up me-2"></i>
                                            Send Password Reset
                                        </>
                                    )}
                                </button>
                            )}

                            <div className="d-inline-block w-100">
                                <button
                                    className="btn btn-outline-danger w-100"
                                    onClick={canDeleteUser() ? deleteUser : undefined}
                                    disabled={!canDeleteUser()}
                                    {...(!canDeleteUser() && {
                                        "data-tooltip-id": "delete-tooltip",
                                        "data-tooltip-content": "Cannot delete users with OTHER role"
                                    })}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    Delete User
                                    {!canDeleteUser() && (
                                        <i className="bi bi-lock ms-2"></i>
                                    )}
                                </button>
                                <Tooltip id="delete-tooltip" />
                            </div>
                        </div>
                    </div>

                    <div className="card border-primary shadow-sm">
                        <div className="card-header bg-primary text-white">
                            <div className="small mb-0">
                                <i className="bi bi-info-circle me-2"></i>
                                Important Note
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="p-2"></div>
                            <div className="alert alert-info mb-0">
                                <p>
                                    <strong>Authentication Note:</strong> When enabling authentication,
                                    the default password will be set to <code>ECXuserx1y1z1</code>.
                                </p>
                                <hr/>
                                <p className="mb-0">
                                    For security, use the "Send Password Reset" button to email the user
                                    password reset instructions to their registered email address.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClinicUserSettings;